import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/dist/query/react";
import { API_ENDPOINT } from "../../config";
import { toastMessage } from "../../utils/toastError";

export interface Assignment {
  id: string;
  title: string;
  instructions?: string;
  duration?: number;
  full_marks: number;
  max_pdf_size_mb?: number;
  note?: string;
  pdf_url?: string;
  pdf_name?: string;
  tags?: Array<{ id: string; name: string }>;
  assignment_batches?: Array<{
    id: string;
    batch_id?: string;
    start_date?: string;
    end_date?: string;
    start_time?: string;
    end_time?: string;
    manual_stop?: boolean;
    batch?: { id: string; course_code?: string; name?: string };
  }>;
  is_assigned_to_batch?: boolean;
  /** Pending submissions for the requested batch. Same count as Manage Assignments. */
  pending_count?: number;
}

export interface AssignmentSubmission {
  id: string;
  assignment_id: string;
  student_id: string;
  batch_id: string;
  notes?: string;
  file_url?: string;
  file_name?: string;
  status: string;
  score?: number | null;
  feedback?: string | null;
  submission_review_note?: string | null;
  submission_evaluation_note?: string | null;
  result_pdf_url?: string | null;
  result_pdf_name?: string | null;
  reviewed_at?: string | null;
  reviewed_by_admin_id?: string | null;
  student?: { id: string; name: string; email?: string; mobile_number?: string };
  batch?: { id: string; name: string; course_code?: string };
  assignment?: { id: string; title: string };
  created_at?: string;
}

interface GetAssignmentsResponse {
  entries: Assignment[];
  total: number;
}

interface SubmissionStatusCounts {
  students: number;
  submitted: number;
  not_submitted: number;
  total: number;
  pending: number;
  approved: number;
  evaluated: number;
  rejected: number;
}

interface GetSubmissionsResponse {
  entries: AssignmentSubmission[];
  total: number;
  counts?: SubmissionStatusCounts;
}

export interface AssignmentRosterEntry {
  student: { id: string; name: string; email?: string; mobile_number?: string };
  batch: { id: string; name?: string; course_code?: string } | null;
  submission: AssignmentSubmission | null;
}

interface GetSubmissionRosterResponse {
  entries: AssignmentRosterEntry[];
  total: number;
  counts?: SubmissionStatusCounts;
}

export interface AssignmentSubmissionExportRow {
  name?: string | null;
  email?: string | null;
  mobile_number?: string | null;
  alternate_number?: string | null;
  batch_course_code?: string | null;
  batch_name?: string | null;
  assignment_title?: string | null;
  status?: string | null;
  submitted_at?: string | null;
  file_url?: string | null;
  reviewed_at?: string | null;
  score?: number | null;
  result_pdf_url?: string | null;
}

interface GetSubmissionExportResponse {
  entries: AssignmentSubmissionExportRow[];
}

export interface AssignmentLeaderboardEntry {
  id: string;
  mark: number;
  student?: { id: string; name: string } | null;
  rank: number;
  submittedAt?: string | null;
  batch?: { id: string; name?: string | null; course_code?: string | null } | null;
}

export type AdminLeaderboardScope = "batch" | "institute";

export interface AdminAssignmentLeaderboardResponse {
  entries: AssignmentLeaderboardEntry[];
  batchStudents: number;
  batchRank: number;
  instituteRank: number;
  instituteStudents: number;
  scope?: AdminLeaderboardScope;
  assignment?: Assignment;
}

export interface StudentAssignmentPerformanceEntry {
  id: string;
  assignment_batch_id: string;
  title: string;
  duration?: number | null;
  full_marks: number;
  batch_id: string;
  batch?: { id: string; name?: string | null; course_code?: string | null } | null;
  start_date?: string | null;
  end_date?: string | null;
  start_time?: string | null;
  end_time?: string | null;
  status: string;
  score?: number | null;
  rank?: number | null;
  students?: number;
  submitted_at?: string | null;
}

interface GetStudentAssignmentPerformanceResponse {
  entries: StudentAssignmentPerformanceEntry[];
  total: number;
}

export const assignmentApi = createApi({
  reducerPath: "assignmentApi",
  baseQuery: fetchBaseQuery({
    baseUrl: API_ENDPOINT + "/assignment",
    credentials: "include",
  }),
  tagTypes: ["Assignments"],
  endpoints: (build) => ({
    getAssignments: build.query<
      GetAssignmentsResponse,
      { skip?: number; take?: number; search?: string; batch_id?: string; assigned_only?: boolean }
    >({
      query: (params) => ({ url: "/", method: "GET", params }),
      providesTags: (result, error, arg) => {
        const tags: Array<{ type: "Assignments"; id: string }> = [{ type: "Assignments", id: "LIST" }];
        if (arg.batch_id) {
          tags.push({ type: "Assignments", id: `batch-${arg.batch_id}` });
        }
        return tags;
      },
    }),
    getAssignment: build.query<Assignment, string>({
      query: (id) => ({ url: `/${id}`, method: "GET" }),
    }),
    createAssignment: build.mutation<Assignment, any>({
      query: (body) => ({
        url: "/",
        method: "POST",
        body,
      }),
      transformErrorResponse(baseQueryReturnValue) {
        toastMessage("error", baseQueryReturnValue.data);
        return Promise.reject(baseQueryReturnValue.data);
      },
      transformResponse(baseQueryReturnValue: Assignment) {
        toastMessage("success", "Assignment created");
        return baseQueryReturnValue;
      },
    }),
    updateAssignment: build.mutation<Assignment, { id: string; body: FormData }>({
      query: ({ id, body }) => ({
        url: `/${id}`,
        method: "PUT",
        body,
      }),
      transformErrorResponse(baseQueryReturnValue) {
        toastMessage("error", baseQueryReturnValue.data);
        return Promise.reject(baseQueryReturnValue.data);
      },
      transformResponse(baseQueryReturnValue: Assignment) {
        toastMessage("success", "Assignment updated");
        return baseQueryReturnValue;
      },
    }),
    deleteAssignment: build.mutation<any, string>({
      query: (id) => ({ url: `/${id}`, method: "DELETE" }),
      transformErrorResponse(baseQueryReturnValue) {
        toastMessage("error", baseQueryReturnValue.data);
        return Promise.reject(baseQueryReturnValue.data);
      },
      transformResponse(baseQueryReturnValue) {
        toastMessage("success", "Assignment deleted");
        return baseQueryReturnValue;
      },
    }),
    assignAssignmentBatch: build.mutation<
      any,
      {
        id: string;
        batch_id: string;
        action: "assign" | "unassign";
        start_date?: string;
        end_date?: string;
        start_time?: string;
        end_time?: string;
        manual_stop?: boolean;
      }
    >({
      query: ({ id, ...body }) => ({ url: `/${id}/assign-batch`, method: "PUT", body }),
      invalidatesTags: (result, error, { batch_id }) => [{ type: "Assignments", id: `batch-${batch_id}` }],
      transformErrorResponse(baseQueryReturnValue) {
        toastMessage("error", baseQueryReturnValue.data);
        return Promise.reject(baseQueryReturnValue.data);
      },
      transformResponse(baseQueryReturnValue) {
        toastMessage("success", "Assignment assigned");
        return baseQueryReturnValue;
      },
    }),
    getAdminSubmissions: build.query<
      GetSubmissionsResponse,
      {
        assignment_id?: string;
        batch_id?: string;
        status?: string;
        search?: string;
        skip?: number;
        take?: number;
      }
    >({
      query: (params) => ({ url: "/admin/submissions", method: "GET", params }),
    }),
    getAdminSubmissionRoster: build.query<
      GetSubmissionRosterResponse,
      {
        assignment_id: string;
        batch_id?: string;
        status?: string;
        search?: string;
        skip?: number;
        take?: number;
      }
    >({
      query: (params) => ({ url: "/admin/submissions/roster", method: "GET", params }),
    }),
    submitAssignmentOnBehalf: build.mutation<
      AssignmentSubmission,
      {
        assignment_id: string;
        student_id: string;
        batch_id: string;
        file: File;
        notes?: string;
        acknowledged?: string;
      }
    >({
      query: ({ assignment_id, student_id, batch_id, file, notes, acknowledged }) => {
        const formData = new FormData();
        formData.append("assignment_id", assignment_id);
        formData.append("student_id", student_id);
        formData.append("batch_id", batch_id);
        formData.append("file", file);
        if (notes) formData.append("notes", notes);
        if (acknowledged) formData.append("acknowledged", acknowledged);
        return { url: "/admin/submissions/submit", method: "POST", body: formData };
      },
      transformErrorResponse(baseQueryReturnValue) {
        toastMessage("error", baseQueryReturnValue.data);
        return Promise.reject(baseQueryReturnValue.data);
      },
    }),
    getAssignmentSubmissionsExport: build.query<
      GetSubmissionExportResponse,
      { assignment_id: string; batch_id?: string }
    >({
      query: (params) => ({ url: "/admin/submissions/export", method: "GET", params }),
    }),
    getStudentAssignmentPerformance: build.query<
      GetStudentAssignmentPerformanceResponse,
      { student_id: string; batch_id?: string; skip?: number; take?: number; sort?: string; orderby?: string }
    >({
      query: (params) => ({ url: "/admin/student-performance", method: "GET", params }),
    }),
    getAdminAssignmentLeaderboard: build.query<
      AdminAssignmentLeaderboardResponse,
      { assignment_id: string; batch_id?: string; scope?: AdminLeaderboardScope }
    >({
      query: (params) => ({ url: "/admin/leaderboard", method: "GET", params }),
      transformErrorResponse(baseQueryReturnValue) {
        toastMessage("error", baseQueryReturnValue.data);
        return Promise.reject(baseQueryReturnValue.data);
      },
    }),
    verifySubmission: build.mutation<
      AssignmentSubmission,
      { id: string; status?: string; submission_review_note?: string }
    >({
      query: ({ id, status, submission_review_note }) => {
        const formData = new FormData();
        if (status) formData.append("status", status);
        if (submission_review_note !== undefined) {
          formData.append("submission_review_note", submission_review_note);
        }
        return { url: `/admin/submissions/${id}/verify`, method: "PUT", body: formData };
      },
      transformErrorResponse(baseQueryReturnValue) {
        toastMessage("error", baseQueryReturnValue.data);
        return Promise.reject(baseQueryReturnValue.data);
      },
      transformResponse(baseQueryReturnValue: AssignmentSubmission) {
        toastMessage("success", "Submission reviewed");
        return baseQueryReturnValue;
      },
    }),
    evaluateSubmission: build.mutation<
      AssignmentSubmission,
      {
        id: string;
        score: number;
        submission_evaluation_note?: string;
        resultPdf?: File | null;
      }
    >({
      query: ({ id, score, submission_evaluation_note, resultPdf }) => {
        const formData = new FormData();
        formData.append("score", String(score));
        if (submission_evaluation_note !== undefined) {
          formData.append("submission_evaluation_note", submission_evaluation_note);
        }
        if (resultPdf) formData.append("result_pdf", resultPdf);
        return { url: `/admin/submissions/${id}/evaluate`, method: "PUT", body: formData };
      },
      transformErrorResponse(baseQueryReturnValue) {
        toastMessage("error", baseQueryReturnValue.data);
        return Promise.reject(baseQueryReturnValue.data);
      },
      transformResponse(baseQueryReturnValue: AssignmentSubmission) {
        toastMessage("success", "Submission evaluated");
        return baseQueryReturnValue;
      },
    }),
    revertSubmissionToPending: build.mutation<AssignmentSubmission, string>({
      query: (id) => ({
        url: `/admin/submissions/${id}/revert-pending`,
        method: "PUT",
      }),
      transformErrorResponse(baseQueryReturnValue) {
        toastMessage("error", baseQueryReturnValue.data);
        return Promise.reject(baseQueryReturnValue.data);
      },
      transformResponse(baseQueryReturnValue: AssignmentSubmission) {
        toastMessage("success", "Submission reverted to pending");
        return baseQueryReturnValue;
      },
    }),
  }),
});

export const {
  useGetAssignmentsQuery,
  useGetAssignmentQuery,
  useCreateAssignmentMutation,
  useUpdateAssignmentMutation,
  useDeleteAssignmentMutation,
  useAssignAssignmentBatchMutation,
  useGetAdminSubmissionsQuery,
  useGetAdminSubmissionRosterQuery,
  useSubmitAssignmentOnBehalfMutation,
  useLazyGetAssignmentSubmissionsExportQuery,
  useGetStudentAssignmentPerformanceQuery,
  useLazyGetAdminAssignmentLeaderboardQuery,
  useVerifySubmissionMutation,
  useEvaluateSubmissionMutation,
  useRevertSubmissionToPendingMutation,
} = assignmentApi;
